import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Fuzzle product buy box: gallery, bundle tiers, per-pair color pickers,
 * multi-line add to cart, delivery estimate and the sticky add-to-cart bar.
 */
class FuzzleProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.tierOff = { 1: 0, 2: Number(this.dataset.tier2Off) || 0, 3: Number(this.dataset.tier3Off) || 0 };
    this.addLabel = this.querySelector('[data-add-label]')?.textContent?.trim() || 'Add to cart';
    this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });

    this.#initGallery();
    this.#initDelivery();
    this.#initSizeGuide();

    this.addEventListener('change', (event) => {
      const target = /** @type {HTMLInputElement} */ (event.target);
      if (target.matches('[data-tier]')) this.#update();
      if (target.closest('[data-pair]')) {
        this.#update();
        if (target.closest('[data-pair="1"]')) this.#showVariantMedia(Number(target.value));
      }
    });

    this.querySelector('[data-add]')?.addEventListener('click', () => this.#addToCart());
    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.#addToCart());
    this.#initSticky();
    this.#update();
  }

  get tier() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-tier]:checked'));
    return Number(checked?.value) || 1;
  }

  /** @returns {Array<{id: number, label: string, price: number}>} */
  get selection() {
    const picks = [];
    for (let i = 1; i <= this.tier; i++) {
      const input = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`[data-pair="${i}"] input:checked`)
      );
      const variant = this.variants.find((v) => v.id === Number(input?.value));
      if (variant) picks.push(variant);
    }
    return picks;
  }

  /** @param {number} cents */
  #format(cents) {
    return this.money.format(cents / 100);
  }

  #update() {
    const tier = this.tier;

    for (const pair of this.querySelectorAll('[data-pair]')) {
      const index = Number(/** @type {HTMLElement} */ (pair).dataset.pair);
      /** @type {HTMLElement} */ (pair).hidden = index > tier;
      const checked = /** @type {HTMLInputElement | null} */ (pair.querySelector('input:checked'));
      const name = pair.querySelector('[data-pair-name]');
      if (name) name.textContent = checked?.dataset.label || '';
    }

    const picks = this.selection;
    const was = picks.reduce((sum, v) => sum + v.price, 0);
    const now = Math.max(0, was - (this.tierOff[tier] || 0));

    this.#setText('[data-add-label]', tier > 1 ? `Add ${tier} pairs` : this.addLabel);
    this.#setText('[data-add-price]', this.#format(now));
    this.#setText('[data-sticky-price]', this.#format(now));
    this.#setText('[data-sticky-note]', tier > 1 ? `${tier} pairs · Free US shipping` : '1 pair · Free US shipping');

    const buyNow = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-buy-now]'));
    if (buyNow && picks.length) {
      /** @type {Map<number, number>} */
      const counts = new Map();
      for (const v of picks) counts.set(v.id, (counts.get(v.id) || 0) + 1);
      const cartUrl = typeof Theme !== 'undefined' ? Theme.routes.cart_url : '/cart';
      buyNow.href = `${cartUrl}/${[...counts].map(([id, qty]) => `${id}:${qty}`).join(',')}`;
    }
  }

  /** @param {string} selector @param {string} text */
  #setText(selector, text) {
    const el = this.querySelector(selector);
    if (el) el.textContent = text;
  }

  async #addToCart() {
    const picks = this.selection;
    if (picks.length === 0) return;

    const buttons = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-add], [data-sticky-add]')]);
    const errorEl = this.querySelector('[data-error]');
    if (errorEl) errorEl.textContent = '';
    buttons.forEach((b) => (b.disabled = true));

    /** @type {Map<number, number>} */
    const counts = new Map();
    for (const v of picks) counts.set(v.id, (counts.get(v.id) || 0) + 1);
    const items = [...counts].map(([id, quantity]) => ({ id, quantity }));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const routes = typeof Theme !== 'undefined' ? Theme.routes : { cart_add_url: '/cart/add.js', cart_url: '/cart' };

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => null);
      const lineCart = cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null;

      if (data.status) {
        deferred.resolve({
          cart: lineCart,
          detail: { didError: true, items: cart?.items, source: 'fuzzle-product', sourceId: this.id },
        });
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        if (errorEl) errorEl.textContent = data.description || data.message || 'Something went wrong. Please try again.';
        return;
      }

      deferred.resolve({
        cart: lineCart,
        detail: {
          items: cart?.items,
          source: 'fuzzle-product',
          sourceId: this.id,
          itemCount: picks.length,
          sections: data.sections,
          didError: false,
        },
      });

      // Pages without a cart drawer fall back to the cart page.
      if (!document.querySelector('cart-drawer-component')) window.location.href = routes.cart_url;
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      if (errorEl) errorEl.textContent = 'Network error. Please try again.';
    } finally {
      buttons.forEach((b) => (b.disabled = false));
    }
  }

  /* ---------- Gallery ---------- */
  #initGallery() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    if (!track) return;
    this.track = track;
    this.slides = /** @type {HTMLElement[]} */ ([...track.children]);
    const thumbsList = /** @type {HTMLElement | null} */ (this.querySelector('[data-thumbs]'));
    const thumbs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-thumb]')]);
    const dots = [...this.querySelectorAll('.fz-gallery__dot')];
    const counter = this.querySelector('[data-counter]');

    const go = (/** @type {number} */ index) => {
      const i = (index + this.slides.length) % this.slides.length;
      // Scroll only the track horizontally — never the page.
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
    };
    this.goToSlide = go;

    this.querySelector('[data-prev]')?.addEventListener('click', () => go(this.#currentSlide() - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => go(this.#currentSlide() + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => go(Number(thumb.dataset.thumb))));

    let active = -1;
    const sync = () => {
      const current = this.#currentSlide();
      if (current === active) return;
      active = current;
      thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === current)));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      if (counter) counter.textContent = `${current + 1} / ${this.slides.length}`;
      const thumb = thumbs[current];
      if (thumbsList && thumb) {
        const li = /** @type {HTMLElement} */ (thumb.parentElement);
        const left = li.offsetLeft - (thumbsList.clientWidth - li.clientWidth) / 2;
        thumbsList.scrollTo({ left, behavior: 'smooth' });
      }
      this.#playActiveVideo(current);
    };

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(sync);
      },
      { passive: true }
    );
    sync();
  }

  #currentSlide() {
    if (!this.track) return 0;
    return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
  }

  /** @param {number} index */
  #playActiveVideo(index) {
    this.slides?.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === index) {
        video.muted = true;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  /** @param {number} variantId */
  #showVariantMedia(variantId) {
    const variant = this.variants.find((v) => v.id === variantId);
    if (!variant?.mediaId || !this.slides || !this.goToSlide) return;
    const index = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.mediaId));
    if (index >= 0) this.goToSlide(index);
  }

  /* ---------- Size guide ---------- */
  #initSizeGuide() {
    this.querySelector('[data-size-guide]')?.addEventListener('click', (event) => {
      const target = /** @type {HTMLDetailsElement | null} */ (document.getElementById('FzSizeGuide'));
      if (!target) return;
      event.preventDefault();
      target.open = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /* ---------- Delivery estimate ---------- */
  #initDelivery() {
    const el = this.querySelector('[data-delivery-dates]');
    if (!el) return;
    const min = Number(this.dataset.processingMin) + Number(this.dataset.transitMin);
    const max = Number(this.dataset.processingMax) + Number(this.dataset.transitMax);
    const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    el.textContent = `${fmt.format(addBusinessDays(new Date(), min))} – ${fmt.format(addBusinessDays(new Date(), max))}`;

    const holiday = /** @type {HTMLElement | null} */ (this.querySelector('[data-holiday]'));
    const cutoff = new Date(`${this.dataset.holidayCutoff}T23:59:59`);
    if (holiday && !Number.isNaN(cutoff.getTime())) holiday.hidden = new Date() > cutoff;
  }

  /* ---------- Sticky bar ---------- */
  #initSticky() {
    const sticky = this.querySelector('[data-sticky]');
    const mainButton = this.querySelector('[data-add]');
    if (!sticky || !mainButton || !('IntersectionObserver' in window)) return;

    let pastButton = false;
    let footerVisible = false;
    const render = () => {
      const show = pastButton && !footerVisible;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
    };

    new IntersectionObserver(([entry]) => {
      pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      render();
    }).observe(mainButton);

    const footer = document.querySelector('footer, #shopify-section-footer, .shopify-section-group-footer-group');
    if (footer) {
      new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        render();
      }).observe(footer);
    }
  }
}

/** @param {Date} date @param {number} days */
function addBusinessDays(date, days) {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return result;
}

if (!customElements.get('fuzzle-product')) customElements.define('fuzzle-product', FuzzleProduct);
